import { CONFIG } from './config.js';
import pawPrintLogo from '/pawprint-modern-updated-phone.png';

const CANVAS_SIZE = 1080;

// Cache the logo image so we don't re-fetch it on every text change
let cachedLogo = null;
let cachedLogoPromise = null;

function loadLogo() {
  if (cachedLogo) return Promise.resolve(cachedLogo);
  if (cachedLogoPromise) return cachedLogoPromise;

  cachedLogoPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      cachedLogo = img;
      resolve(img);
    };
    img.onerror = reject;
    img.src = pawPrintLogo;
  });

  return cachedLogoPromise;
}

function loadBackground(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Helper function to draw rounded rectangle (for browser compatibility)
function drawRoundedRect(ctx, x, y, width, height, radius) {
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, radius);
  } else {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }
}

/**
 * Renders the Instagram graphic onto the given canvas context.
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} storyData
 * @param {number} canvasSize - defaults to 1080
 * @returns {Promise<void>}
 */
export async function renderCanvas(ctx, storyData, canvasSize = CANVAS_SIZE) {
  if (!storyData.backgroundImage) return;

  // Wait for fonts before measuring
  await document.fonts.ready;

  const [logoImg, bgImg] = await Promise.all([
    loadLogo(),
    loadBackground(storyData.backgroundImage),
  ]);

  // Clear canvas
  ctx.clearRect(0, 0, canvasSize, canvasSize);

  // Draw background with cover cropping
  const imgAspect = bgImg.width / bgImg.height;
  const canvasAspect = 1; // square

  let sourceX = 0;
  let sourceY = 0;
  let sourceWidth = bgImg.width;
  let sourceHeight = bgImg.height;

  if (imgAspect > canvasAspect) {
    sourceWidth = bgImg.height * canvasAspect;
    sourceX = (bgImg.width - sourceWidth) / 2;
  } else {
    sourceHeight = bgImg.width / canvasAspect;
    sourceY = (bgImg.height - sourceHeight) / 2;
  }

  ctx.drawImage(bgImg, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvasSize, canvasSize);

  const fontFamily = 'Urbanist, sans-serif';
  const padding = CONFIG.spacing.padding;
  const topPadding = CONFIG.spacing.topPadding;
  const logoHeight = CONFIG.logo.height;
  const isBottomLayout = storyData.titlePosition === 'bottom';

  // Measure headline line count
  const headlineText = (storyData.headline || '').trimStart();
  ctx.font = `600 ${CONFIG.fontSizes.headline}px ${fontFamily}`;
  const maxWidth = canvasSize - (padding * 2);
  const words = headlineText.split(' ').filter(word => word.length > 0);
  let testLine = '';
  let lineCount = 1;

  for (let i = 0; i < words.length; i++) {
    const testText = testLine + words[i] + ' ';
    const metrics = ctx.measureText(testText);
    if (metrics.width > maxWidth && i > 0) {
      lineCount++;
      testLine = words[i] + ' ';
    } else {
      testLine = testText;
    }
  }

  const useSmallFont = lineCount > 3;
  const headlineFontSize = useSmallFont ? CONFIG.fontSizes.headlineSmall : CONFIG.fontSizes.headline;
  const lineHeight = useSmallFont ? CONFIG.lineHeights.headlineSmall : CONFIG.lineHeights.headline;

  ctx.font = `600 ${headlineFontSize}px ${fontFamily}`;

  // Calculate subtitle height for bottom layout
  let subtitleHeight = 0;
  const subtitleTextRaw = (storyData.subtitle || '').trimStart();
  if (subtitleTextRaw && isBottomLayout) {
    ctx.font = `400 ${CONFIG.fontSizes.subtitle}px ${fontFamily}`;
    const subtitleWords = subtitleTextRaw.split(' ').filter(word => word.length > 0);
    let subtitleTestLine = '';
    let subtitleLines = 1;
    for (let i = 0; i < subtitleWords.length; i++) {
      const testText = subtitleTestLine + subtitleWords[i] + ' ';
      const metrics = ctx.measureText(testText);
      if (metrics.width > maxWidth && i > 0) {
        subtitleLines++;
        subtitleTestLine = subtitleWords[i] + ' ';
      } else {
        subtitleTestLine = testText;
      }
    }
    const subtitleLineHeight = CONFIG.fontSizes.subtitle * 1.3;
    subtitleHeight = CONFIG.spacing.subtitleSpacing + (subtitleLines - 1) * subtitleLineHeight + CONFIG.fontSizes.subtitle;
    ctx.font = `600 ${headlineFontSize}px ${fontFamily}`;
  }

  // Headline position
  let headlineStartY;
  let headlineEndY;

  if (isBottomLayout) {
    const ctaY = canvasSize - CONFIG.spacing.ctaBottom;
    headlineEndY = ctaY - 100 - subtitleHeight;
    headlineStartY = headlineEndY;
  } else {
    headlineStartY = topPadding + logoHeight + CONFIG.spacing.headlineSpacing + headlineFontSize;
    headlineEndY = headlineStartY;
  }

  testLine = '';
  for (let i = 0; i < words.length; i++) {
    const testText = testLine + words[i] + ' ';
    const metrics = ctx.measureText(testText);
    if (metrics.width > maxWidth && i > 0) {
      if (isBottomLayout) {
        headlineStartY -= lineHeight;
      } else {
        headlineEndY += lineHeight;
      }
      testLine = words[i] + ' ';
    } else {
      testLine = testText;
    }
  }

  if (!isBottomLayout) {
    headlineEndY += lineHeight;
  }

  const headlineEndPercent = headlineEndY / canvasSize;

  // Gradient overlay
  const gradient = isBottomLayout
    ? ctx.createLinearGradient(0, canvasSize, 0, 0)
    : ctx.createLinearGradient(0, 0, 0, canvasSize);

  if (isBottomLayout) {
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0.85)');
    gradient.addColorStop(0.15, 'rgba(0, 0, 0, 0.75)');
    gradient.addColorStop(0.35, 'rgba(0, 0, 0, 0.5)');
    gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.25)');
    gradient.addColorStop(0.65, 'rgba(0, 0, 0, 0.35)');
    gradient.addColorStop(0.85, 'rgba(0, 0, 0, 0.55)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.7)');
  } else {
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0.82)');
    const headlineMid = headlineEndPercent * 0.5;
    const fadeThroughHeadline = 0.82 - (headlineMid * 0.35);
    gradient.addColorStop(headlineMid, `rgba(0, 0, 0, ${fadeThroughHeadline.toFixed(2)})`);
    const afterHeadlinePoint = Math.min(0.38, headlineEndPercent + 0.08);
    gradient.addColorStop(afterHeadlinePoint, 'rgba(0, 0, 0, 0.52)');
    gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.2)');
    gradient.addColorStop(0.65, 'rgba(0, 0, 0, 0.3)');
    gradient.addColorStop(0.8, 'rgba(0, 0, 0, 0.55)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.75)');
  }
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvasSize, canvasSize);

  // Logo
  const logoAspect = logoImg.width / logoImg.height;
  const logoWidth = logoHeight * logoAspect;
  const logoX = (canvasSize - logoWidth) / 2;
  const logoY = topPadding;
  ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);

  // Headline
  ctx.font = `600 ${headlineFontSize}px ${fontFamily}`;
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  let line = '';
  let y = headlineStartY;

  for (let i = 0; i < words.length; i++) {
    const testText = line + words[i] + ' ';
    const metrics = ctx.measureText(testText);
    if (metrics.width > maxWidth && i > 0) {
      ctx.fillText(line, padding, y);
      line = words[i] + ' ';
      y += lineHeight;
    } else {
      line = testText;
    }
  }
  ctx.fillText(line, padding, y);

  let textBottomY = y;

  // Subtitle
  if (subtitleTextRaw) {
    const subtitleText = subtitleTextRaw;
    ctx.font = `400 ${CONFIG.fontSizes.subtitle}px ${fontFamily}`;
    ctx.fillStyle = '#FFFFFF';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';

    let subtitleY = y + CONFIG.spacing.subtitleSpacing + CONFIG.fontSizes.subtitle;

    if (isBottomLayout) {
      const ctaY = canvasSize - CONFIG.spacing.ctaBottom;
      const maxSubtitleY = ctaY - 80;
      if (subtitleY > maxSubtitleY) {
        subtitleY = maxSubtitleY;
      }
    }

    const subtitleWords = subtitleText.split(' ').filter(word => word.length > 0);
    let subtitleLine = '';
    const subtitleLineHeight = CONFIG.fontSizes.subtitle * 1.3;

    for (let i = 0; i < subtitleWords.length; i++) {
      const testText = subtitleLine + subtitleWords[i] + ' ';
      const metrics = ctx.measureText(testText);
      if (metrics.width > maxWidth && i > 0) {
        ctx.fillText(subtitleLine, padding, subtitleY);
        subtitleLine = subtitleWords[i] + ' ';
        subtitleY += subtitleLineHeight;
      } else {
        subtitleLine = testText;
      }
    }
    ctx.fillText(subtitleLine, padding, subtitleY);
    textBottomY = subtitleY;
  }

  // Category and byline
  let categoryY;

  if (isBottomLayout) {
    let textBlockTop = headlineStartY - headlineFontSize;
    if (subtitleTextRaw) {
      // subtitle is below headline in bottom layout, so recalculate top
      textBlockTop = headlineStartY - headlineFontSize;
    }
    categoryY = textBlockTop - 5 - 58;
    categoryY = Math.max(CONFIG.spacing.padding, categoryY);
  } else {
    categoryY = textBottomY + CONFIG.spacing.categorySpacing;
  }

  const categoryText = storyData.category || 'Category';
  ctx.font = `300 ${CONFIG.fontSizes.category}px ${fontFamily}`;
  const categoryMetrics = ctx.measureText(categoryText);
  const pillPadding = CONFIG.categoryPill.padding;
  const pillWidth = categoryMetrics.width + (pillPadding * 2);
  const pillHeight = 58;

  ctx.fillStyle = '#FF7201';
  drawRoundedRect(ctx, padding, categoryY, pillWidth, pillHeight, CONFIG.categoryPill.cornerRadius);
  ctx.fill();

  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(categoryText, padding + pillWidth / 2, categoryY + pillHeight / 2);
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';

  const bylineText = `by ${storyData.authorName || 'Author'}${storyData.authorRole ? `, ${storyData.authorRole}` : ''}`;
  ctx.font = `300 ${CONFIG.fontSizes.byline}px ${fontFamily}`;
  ctx.fillStyle = '#FFFFFF';
  ctx.textBaseline = 'middle';
  const bylineX = padding + pillWidth + 20;
  ctx.fillText(bylineText, bylineX, categoryY + pillHeight / 2);
  ctx.textBaseline = 'top';

  // CTA
  const ctaText = `read at woodsidepawprint.com (link in bio)`;
  ctx.font = `300 italic ${CONFIG.fontSizes.cta}px ${fontFamily}`;
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  const ctaY = canvasSize - CONFIG.spacing.ctaBottom;
  ctx.fillText(ctaText, canvasSize / 2, ctaY);
}
